import { action, flow, makeObservable, observable } from 'mobx';
import { DrawService } from '../services/drawService';
import { GlobalStore, GridStore, ApiResponse } from '@roc/feature-app-core';
import { Draw, DrawDTO } from '../types/draws';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { DrawFormStore } from './drawFormStore';
import { DrawInformationFormStore } from './drawInformationFormStore';
import { WireAuthorizationFormStore } from './wireAuthorizationFormStore';
import { InspectionInvoiceDetailsFormStore } from './inspectionInvoiceDetailsFormStore';
import { DrawActionsStore } from './drawActionsStore';
import { AddDrawFormStore } from './addDrawFormStore';

export class DrawStore {
  globalStore: GlobalStore;
  private drawService: DrawService;
  public drawFormStore: DrawFormStore;
  drawInformationFormStore: DrawInformationFormStore;
  wireAuthorizationFormStore: WireAuthorizationFormStore;
  inspectionInvoiceDetailsFormStore: InspectionInvoiceDetailsFormStore;
  drawList: Draw[] = [];
  bankInformation: any = null;
  wireAuthorizationLink: string = null;
  documentStatus: { [key: string]: string } | null = {};
  drawListKey = 1;//to refresh grid component when list is updated
  currentDraw: Draw = null;
  drawDTO: DrawDTO = null;
  scopeOfWorkList: any[] = [];
  revisionList: any[] = [];
  myDrawsGridStore: GridStore;
  propertyPhotosUrl: string;
  isDrawDetailsModalOpen: boolean;

  public drawActionsStore: DrawActionsStore;
  public addDrawFormStore: AddDrawFormStore;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.drawService = new DrawService();
    this.drawFormStore = new DrawFormStore(globalStore);
    this.wireAuthorizationFormStore = new WireAuthorizationFormStore(globalStore);
    this.inspectionInvoiceDetailsFormStore = new InspectionInvoiceDetailsFormStore(globalStore);
    this.myDrawsGridStore = new GridStore(
      () => this.fetchAllDraws(),
      null,
      50,
    );
    this.drawInformationFormStore = new DrawInformationFormStore(globalStore);
    this.drawActionsStore = new DrawActionsStore(globalStore);
    this.addDrawFormStore = new AddDrawFormStore(globalStore);

    makeObservable(this, {
      fetchDraws: flow,
      fetchDrawDTOById: flow,
      resetDraws: action,
      drawList: observable,
      drawListKey: observable,
      drawFormStore: observable,
      documentStatus: observable,
      currentDraw: observable,
      scopeOfWorkList: observable,
      revisionList: observable,
      fetchSOWDocuments: flow,
      fetchDocumentStatus: flow,
      getBankInfo: flow,
      bankInformation: observable,
      wireAuthorizationLink: observable,
      fetchWireAuthorizationLink: flow,
      cancelRevision: flow,
      cancelDrawRequest: flow,
      propertyPhotosUrl: observable,
      drawInformationFormStore: observable,
      wireAuthorizationFormStore: observable,
      isDrawDetailsModalOpen: observable,
      setIsDrawDetailsModalOpen: action,
      fetchDrawsAndRevisions: flow
    });
  }

  *cancelDrawRequest(drawId: number, loanId: string) {
    try {
      const response: ApiResponse = yield this.drawService.cancelDrawRequest(drawId);
      const data = JSON.parse(response.data?.data);
      if (data.success) {
        if (this.globalStore.isInternalPortal) {
          this.fetchDraws(loanId, true);
        } else {
          this.fetchDrawsAndRevisions(loanId);
        }
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Cancel request was made successfully.',
        });
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while canceling draw request.',
      });
    }
  }

  *cancelRevision(data: any) {
    try {
      const response: ApiResponse = yield this.drawService.cancelDrawRequest(data.drawId);
      const resposeData = JSON.parse(response.data?.data);
      if (resposeData.success) {
        this.fetchSOWDocuments(data.loanId);
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'The SOW revision was successfully cancelled.',
        });
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while canceling SOW revision.',
      });
    }
  }
  *fetchSOWDocuments(loanId: string, callback?: (success: boolean) => void) {
    try {
      const response: ApiResponse = yield this.drawService.getSOWDocuments(
        loanId
      );
      const { data } = response;
      const swoData = JSON.parse(data?.data);
      this.scopeOfWorkList = swoData.sowList ? swoData.sowList : [];
      this.revisionList = swoData.revisionList ? swoData.revisionList : [];
      callback && callback(true);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting SOW related documents.',
      });
      callback && callback(false);
    }
  }
  *fetchDocumentStatus(loanId: string, fileName: string) {
    try {
      const response: ApiResponse = yield this.drawService.getDocumentStatus(loanId, fileName);
      const { data } = response;
      const docStatus = data?.data;
      this.documentStatus[fileName] = docStatus;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting document status.',
      });
    }
  }
  private fetchWireAuthorizationRequestID = async (loanId: number) => {
    try {
      const response: ApiResponse = await this.drawService.getWireAuth(loanId);
      const { data } = response;
      return data?.rows?.[0]?.requestId || null;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching wire authorization request ID.',
      });
    }
  }
  *fetchWireAuthorizationLink(loanId: number) {
    try {
      const wireRequestId = yield this.fetchWireAuthorizationRequestID(loanId);
      if (!wireRequestId) {
        return;
      }
      const response: ApiResponse = yield this.drawService.getWireAuthRequestId(wireRequestId);
      const { data } = response;
      this.wireAuthorizationLink = data;
    }
    catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching wire authorization link.',
      });
    }
  }

  *getBankInfo(loanId: number) {
    try {
      const response: ApiResponse = yield this.drawService.getBankInfo(loanId, 'DrawEntity');
      this.bankInformation = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting bank information.',
      })
    }
  }
  *fetchDraws(loanId: string, fetchAllDraws = false) {
    try {
      const response: ApiResponse = yield this.drawService.getDrawsByLoanId(loanId, fetchAllDraws);
      const { data } = response;
      this.drawList = data.data ? data.data : [];
      this.drawListKey = !this.drawListKey ? 1 : this.drawListKey + 1;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting draw list.',
      });
    }
  }

  *fetchDrawsAndRevisions(loanId: string) {
    try {
      const response: ApiResponse = yield this.drawService.getDrawsAndRevisionsByLoanId(loanId);
      const { data } = response;
      this.drawList = data.data ? data.data : [];
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting draw list.',
      });
    }
  }

  private async fetchAllDraws() {
    const gridFilters = this.myDrawsGridStore.gridData.meta.filters ?? {};
    const filters = {
      ...gridFilters,
      ...this.myDrawsGridStore.gridData.meta.dropdownFilters
    };
    try {
      const response: ApiResponse = await this.drawService.getAllDraws(
        this.myDrawsGridStore.gridData.meta.pageNumber,
        this.myDrawsGridStore.gridData.meta.pageSize,
        this.myDrawsGridStore.gridData.meta.sortDir,
        this.myDrawsGridStore.gridData.meta.sortBy,
        filters,
      );
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchDrawDTOById(drawId: string) {
    try {
      const response: ApiResponse = yield this.drawService.getDrawDTOById(drawId);
      const { data } = response;
      this.drawDTO = data.data ? data.data : null;
      this.currentDraw = { ...this.drawDTO?.draw, propertyAddress: this.drawDTO?.propertyAddress };
      this.propertyPhotosUrl = this.drawDTO?.truepicLink;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting draw information.',
      });
    }
  }

  resetDraws() {
    this.drawList = [];
    this.drawInformationFormStore.reset();
    this.wireAuthorizationFormStore.reset();
    this.inspectionInvoiceDetailsFormStore.reset();
    this.isDrawDetailsModalOpen = false;
    this.drawListKey = 1;
  }

  resetCurrentDraw() {
    this.currentDraw = null;
    this.drawInformationFormStore.reset();
    this.wireAuthorizationFormStore.reset();
    this.inspectionInvoiceDetailsFormStore.reset();
    this.isDrawDetailsModalOpen = false;
  }

  resetRevisionDocs() {
    this.scopeOfWorkList = [];
    this.revisionList = [];
    this.drawInformationFormStore.reset();
    this.wireAuthorizationFormStore.reset();
    this.inspectionInvoiceDetailsFormStore.reset();
    this.isDrawDetailsModalOpen = false;
  }

  setIsDrawDetailsModalOpen(open: boolean) {
    this.isDrawDetailsModalOpen = open;
  }
}
